import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { Props } from 'recharts/types/component/Label'

import type { DirectPurchaseChartProps } from './DirectPurchaseChart.types'

// Helper function to format large numbers for axis (e.g., 10000 -> 10k)
const formatAxisNumbers = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`
  }
  return value.toFixed(0)
}

// Helper function to format price in euros
const formatPrice = (euros: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(euros)
}

// Custom tooltip component
type CustomTooltipProps = {
  active?: boolean
  payload?: Array<{
    value: number
    payload: {
      year: number
      consumptionInEur: number
      retrofitInEur: number
      savingsInEur: number
    }
  }>
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload[0]) {
    const data = payload[0].payload
    return (
      <Box
        sx={{
          padding: '8px',
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0px 3px 9px 0px rgba(0, 0, 0, 0.18)',
          fontSize: '11px',
        }}
      >
        <Typography
          sx={{
            marginBottom: '4px',
            fontWeight: 700,
            color: '#6a707e',
            fontSize: '12px',
          }}
        >
          {data.year}
        </Typography>

        {/* Energiekosten saniert (akk.) - green */}
        <Box
          sx={{
            color: '#0E8C4A',
            fontWeight: 500,
            marginTop: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten saniert (akk.):</span>
          <span>{formatPrice(data.retrofitInEur)}</span>
        </Box>

        {/* Energiekosten unsaniert (akk.) - blue */}
        <Box
          sx={{
            color: '#366192',
            fontWeight: 500,
            marginTop: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten unsaniert (akk.):</span>
          <span>{formatPrice(data.consumptionInEur)}</span>
        </Box>

        {/* Bisher gespart - separator and bold */}
        <Box
          sx={{
            color: '#181d29',
            paddingTop: '4px',
            marginTop: '4px',
            borderTop: '1px solid #74777f',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Bisher gespart:</span>
          <span>{formatPrice(data.savingsInEur)}</span>
        </Box>
      </Box>
    )
  }
  return null
}

// Custom legend component
const CustomLegend = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        paddingTop: 1,
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: '16px',
          height: '16px',
          backgroundColor: '#15C46C',
          borderRadius: '2px',
        }}
      />
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 500,
          color: '#464A54',
        }}
      >
        Energiekosteneinsparung
      </Typography>
    </Box>
  )
}

const DirectPurchaseChart = ({ financing, retrofitName }: DirectPurchaseChartProps) => {
  const { netRetrofitCostsInCt, energyCostTimeSeries } = financing

  // Calculate cumulative data: accumulate consumption, retrofit, and savings over time
  const chartData = useMemo(() => {
    // Accumulate values over time
    let cumulativeConsumptionInEur = 0
    let cumulativeRetrofitInEur = 0
    let cumulativeSavingsInEur = 0

    return energyCostTimeSeries.map(consumptionItem => {
      const consumptionCost = consumptionItem.energyCostsInCt
      const retrofitCost = consumptionItem.energyCostsAppliedRetrofitInCt
      const yearlySavingsInCt = consumptionCost - retrofitCost

      // Convert to euros
      const consumptionInEur = consumptionCost / 100
      const retrofitInEur = retrofitCost / 100
      const yearlySavingsInEur = yearlySavingsInCt / 100

      // Accumulate all values
      cumulativeConsumptionInEur += consumptionInEur
      cumulativeRetrofitInEur += retrofitInEur
      cumulativeSavingsInEur += yearlySavingsInEur

      return {
        year: consumptionItem.year,
        consumptionInEur: cumulativeConsumptionInEur,
        retrofitInEur: cumulativeRetrofitInEur,
        savingsInEur: cumulativeSavingsInEur,
      }
    })
  }, [energyCostTimeSeries])

  // Calculate retrofit cost in euros for reference line
  const retrofitCostInEur = netRetrofitCostsInCt / 100

  // Find amortization point (where savings equals retrofit cost)
  // Interpolates between years when amortization happens mid-year
  const amortizationPoint = useMemo(() => {
    if (!retrofitCostInEur) return null

    // Find the index where cumulative savings first exceeds retrofit cost
    const crossoverIndex = chartData.findIndex(data => data.savingsInEur >= retrofitCostInEur)

    // If no crossover found or it happens at the first data point
    if (crossoverIndex === -1) return null
    if (crossoverIndex === 0) {
      return {
        year: chartData[0].year,
        value: retrofitCostInEur,
      }
    }

    // Get the two points where the crossover happens
    const pointBefore = chartData[crossoverIndex - 1]
    const pointAfter = chartData[crossoverIndex]

    // Linear interpolation to find exact year where savings equals retrofit cost
    const savingsDiff = pointAfter.savingsInEur - pointBefore.savingsInEur
    const costDiff = retrofitCostInEur - pointBefore.savingsInEur
    const yearDiff = pointAfter.year - pointBefore.year

    // Calculate interpolated year
    const interpolatedYear = pointBefore.year + (costDiff / savingsDiff) * yearDiff

    return {
      year: interpolatedYear,
      value: retrofitCostInEur,
    }
  }, [chartData, retrofitCostInEur])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '300px',
          '& *:focus': {
            outline: 'none',
          },
          '& svg': {
            outline: 'none',
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 12,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="1 0" vertical={false} stroke="#E1E1E4" />
            <XAxis
              strokeWidth={0}
              dataKey="year"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: '#74777F', fontSize: 12 }}
              tickLine={false}
            />
            <YAxis
              strokeWidth={0}
              width={40}
              tickFormatter={formatAxisNumbers}
              tick={{ fill: '#74777F', fontSize: 12 }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Area with stroke for top edge and fill */}
            <Area
              type="monotone"
              dataKey="savingsInEur"
              stroke="#5BC06B"
              fill="#1CFB8E33"
              strokeWidth={2}
              isAnimationActive={false}
            />
            {/* Reference line at y=0 for bottom edge */}
            <ReferenceLine y={0} stroke="#5BC06B" strokeWidth={2} />
            {/* Reference line for retrofit costs */}
            {retrofitCostInEur && (
              <ReferenceLine y={retrofitCostInEur} stroke="#37000B" strokeWidth={2}>
                <Label
                  value={`Kosten ${retrofitName}`}
                  position="insideBottomLeft"
                  offset={10}
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    fill: '#464A54',
                  }}
                />
              </ReferenceLine>
            )}
            {/* Amortization point marker */}
            {amortizationPoint && (
              <ReferenceDot
                x={amortizationPoint.year}
                y={amortizationPoint.value}
                r={3}
                fill="#125350"
                stroke="#37BFC5"
                strokeWidth={2}
              >
                <Label
                  position="centerBottom"
                  content={(props: Props) => {
                    const { viewBox } = props
                    if (!viewBox || !('x' in viewBox)) return null

                    const { x, y, width, height } = viewBox

                    return (
                      <g>
                        {/* Triangle arrow pointing upward - 16x8px with rounded apex */}
                        <path
                          d={`M ${x + width / 2 - 8} ${y + height / 2 + 8} L ${x + width / 2 - 2} ${y + height / 2 + 2} Q ${x + width / 2} ${y + height / 2} ${x + width / 2 + 2} ${y + height / 2 + 2} L ${x + width / 2 + 8} ${y + height / 2 + 8} Z`}
                          fill="#181D29"
                        />
                        {/* Label background - positioned below */}
                        <rect
                          x={x + width / 2 - 48}
                          y={y + height / 2 + 7}
                          width={96}
                          height={20}
                          fill="#181D29"
                          rx={8}
                        />
                        {/* Label text */}
                        <text
                          x={x + width / 2}
                          y={y + height / 2 + 21}
                          fill="#EDEDF2"
                          fontSize={12}
                          fontWeight={500}
                          textAnchor="middle"
                        >
                          Amortisation
                        </text>
                      </g>
                    )
                  }}
                />
              </ReferenceDot>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      <CustomLegend />
    </Box>
  )
}

export default DirectPurchaseChart
